import { getArmBaseUrl } from './armConfig';
import { telemetryService } from './telemetryService';
import type { AzurePickerPage, AzurePickerResourceType, AzurePickerScope, AzurePickerTarget } from '@/types/azureResourcePicker';

const PAGE_SIZE = 100;
const REQUEST_TIMEOUT = 30_000;
const GRAPH_PATH = '/providers/Microsoft.ResourceGraph/resources?api-version=2021-03-01';

async function request<T>(token: string, path: string, signal: AbortSignal, body?: unknown): Promise<T> {
  const base = new URL(getArmBaseUrl());
  const url = new URL(path, `${base.href.replace(/\/$/, '')}/`);
  if (url.origin !== base.origin || url.username || url.password || url.hash ||
    !url.pathname.startsWith(`${base.pathname.replace(/\/$/, '')}/`)) {
    throw new Error('Azure returned an invalid resource discovery URL.');
  }

  let response: Response;
  try {
    response = await fetch(url.href, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: AbortSignal.any([signal, AbortSignal.timeout(REQUEST_TIMEOUT)]),
      redirect: 'error',
    });
  } catch (error) {
    if (!signal.aborted) telemetryService.trackEvent('azureResourcePicker.discoveryFailed', { status: 'transport' });
    throw error;
  }

  // The response body is never read on failure, so Azure error text cannot reach the UI.
  if (!response.ok) {
    telemetryService.trackEvent('azureResourcePicker.discoveryFailed', { status: response.status });
    if (response.status === 401) throw new Error('Sign in again to browse Azure resources.');
    if (response.status === 403) throw new Error('You do not have permission to browse this Azure scope. You can still enter an ID manually.');
    if (response.status === 429) throw new Error('Azure is limiting discovery requests. Try again shortly.');
    throw new Error(`Azure resource discovery failed (${response.status}). Try again.`);
  }
  return await response.json() as T;
}

function armPath(path: string): string {
  return `${getArmBaseUrl().replace(/\/$/, '')}${path}`;
}

export async function listPickerSubscriptions(token: string, signal: AbortSignal, continuation?: string): Promise<AzurePickerPage<AzurePickerTarget>> {
  const response = await request<{
    value: { id: string; displayName: string; subscriptionId: string }[];
    nextLink?: string;
  }>(token, continuation ?? armPath('/subscriptions?api-version=2022-12-01'), signal);
  return {
    items: response.value.map((item) => ({ id: item.id, name: item.displayName, subscriptionId: item.subscriptionId, kind: 'subscription' })),
    ...(response.nextLink ? { continuation: response.nextLink } : {}),
  };
}

export async function listPickerResourceGroups(token: string, scope: AzurePickerScope, signal: AbortSignal, continuation?: string): Promise<AzurePickerPage<AzurePickerTarget>> {
  const response = await request<{
    value: { id: string; name: string; location: string }[];
    nextLink?: string;
  }>(token, continuation ?? armPath(`/subscriptions/${encodeURIComponent(scope.subscriptionId)}/resourcegroups?api-version=2021-04-01`), signal);
  return {
    items: response.value.map((item) => ({ ...item, subscriptionId: scope.subscriptionId, resourceGroup: item.name, kind: 'resourceGroup' })),
    ...(response.nextLink ? { continuation: response.nextLink } : {}),
  };
}

function literal(value: string): string {
  return JSON.stringify(value);
}

function scopeQuery(scope: AzurePickerScope): string {
  const clauses = ['Resources'];
  if (scope.resourceGroup) clauses.push(`where resourceGroup =~ ${literal(scope.resourceGroup)}`);
  if (scope.allowedResourceTypes !== undefined) {
    clauses.push(scope.allowedResourceTypes.length
      ? `where type in~ (${scope.allowedResourceTypes.map(literal).join(', ')})`
      : 'where false');
  }
  return clauses.join(' | ');
}

export async function listPickerResourceTypes(token: string, scope: AzurePickerScope, signal: AbortSignal, continuation?: string): Promise<AzurePickerPage<AzurePickerResourceType>> {
  const query = [scopeQuery(scope), 'summarize count = count() by type',
    ...(continuation ? [`where type > ${literal(continuation)}`] : []), 'order by type asc'].join(' | ');
  const response = await request<{ data: AzurePickerResourceType[] }>(token, armPath(GRAPH_PATH), signal, {
    subscriptions: [scope.subscriptionId], query, options: { resultFormat: 'objectArray', $top: PAGE_SIZE + 1 },
  });
  const items = response.data.slice(0, PAGE_SIZE);
  return {
    items,
    ...(response.data.length > PAGE_SIZE ? { continuation: items[items.length - 1]!.type } : {}),
  };
}

export async function listPickerResources(token: string, scope: AzurePickerScope, signal: AbortSignal, continuation?: string): Promise<AzurePickerPage<AzurePickerTarget>> {
  const query = [scopeQuery(scope),
    ...(scope.resourceType ? [`where type =~ ${literal(scope.resourceType)}`] : []),
    'project id, name, type, resourceGroup, subscriptionId, location', 'order by name asc, id asc'].join(' | ');
  const response = await request<{ data: Omit<AzurePickerTarget, 'kind'>[]; $skipToken?: string; totalRecords: number }>(token, armPath(GRAPH_PATH), signal, {
    subscriptions: [scope.subscriptionId], query,
    options: { resultFormat: 'objectArray', $top: PAGE_SIZE, ...(continuation ? { $skipToken: continuation } : {}) },
  });
  if (!continuation && !response.$skipToken && response.totalRecords > response.data.length) {
    throw new Error('Azure returned incomplete results. Narrow the resource filters and retry.');
  }
  return {
    items: response.data.map((item) => ({ ...item, kind: 'resource' })),
    ...(response.$skipToken ? { continuation: response.$skipToken } : {}),
  };
}